import {
  Accordion,
  Button,
  Icon,
  Input,
  InputAddon,
  InputIcon,
  Table,
  TableColumn,
} from '@components';
import { monthsChipsEnum } from 'enums';
import ConsolidatedBudgetAPICaller from '@services/api/consolidatedBudget';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatCurrency } from 'utils/string/currency';
import { validateUUIDFormat } from 'utils/string/uuid';
import { BudgetTemplate } from 'interfaces/BudgetTemplate';
import BudgetTemplateAPICaller from '@services/api/budgetTemplate';
import { useQueryParams } from '@hooks/useQueryParams';
import { CategoryDetailModal } from './CategoryDetailModal';
import { getPercentageClass } from 'utils/number';
import { CostCenter } from 'interfaces/CostCenter';
import pinned from '@assets/icons/pinned.svg';
import unpinned from '@assets/icons/unpinned.svg';
import { useForm } from 'react-hook-form';

type Summary = {
  id: string;
  total: number;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
};

type EvolutionData = {
  month: string;
  totalBudget: number;
  totalRealized: number;
  businessPartner: string;
  percentageUsage: number;
};

type BudgetConsolidatedDetails = {
  costCenter: CostCenter;
  budgetSummary: Summary;
  realizedSummary: Summary;
  months: EvolutionData[];
};

type BudgetConsolidatedMonthDetails = [
  {
    id: string;
    name: string;
    description: string;
    businessPartner: string;
    totalBudget: number;
    totalRealized: number;
    percentageUsage: number;
  }
];

export default function ConsolidatedBudgetDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useForm();
  const { budgetTemplateId, costCenterId } = useParams();
  const { type } = location.state;
  const [budgetTemplate, setBudgetTemplate] = useState<BudgetTemplate>();

  const [filter, setFilter] = useState('');
  const [expandAll, setExpandAll] = useState(false);
  const [data, setData] = useState<BudgetConsolidatedDetails>({} as any);
  const [monthData, setMonthData] = useState<{
    [key: string]: BudgetConsolidatedMonthDetails;
  }>();

  const queryParams = useQueryParams();
  const month = queryParams.get('month');
  const categoryId = queryParams.get('categoryId');
  const categoryName = queryParams.get('categoryName');

  const [showingCategoryModal, setShowingCategoryModal] = useState(false);

  const loadCostCenterBudget = useCallback(
    ({
      budgetTemplateId,
      costCenterId,
      type,
    }: {
      budgetTemplateId: string;
      costCenterId: string;
      type: string | null;
    }) => {
      ConsolidatedBudgetAPICaller.listCostCenterBudget({
        budgetTemplateId,
        costCenterId,
        type: type as 'income' | 'expense',
      })
        .then((values) => {
          setData(values.result);
          console.log(values.result.categoriesByMonth);
          setMonthData(values.result.categoriesByMonth);
        })
        .catch(() => {
          toast.error('Erro ao carregar orçamento consolidado.');
          navigate(-1);
        });
    },
    [navigate, setData]
  );

  const loadBudgetTemplate = useCallback(
    (budgetTemplateId: string) => {
      BudgetTemplateAPICaller.fetch(budgetTemplateId)
        .then((values) => {
          setBudgetTemplate(values);
        })
        .catch(() => {
          toast.error('Erro ao carregar modelo de orçamento.');
          navigate(-1);
        });
    },
    [navigate]
  );

  useEffect(() => {
    if (budgetTemplateId && costCenterId) {
      if (
        validateUUIDFormat(budgetTemplateId) &&
        validateUUIDFormat(costCenterId)
      ) {
        loadCostCenterBudget({
          budgetTemplateId,
          costCenterId,
          type,
        });

        loadBudgetTemplate(budgetTemplateId);
      } else {
        toast.error('Modelo de orçamento ou centro de custo inválido.');
        navigate(-1);
      }
    }
  }, [
    budgetTemplateId,
    costCenterId,
    loadBudgetTemplate,
    loadCostCenterBudget,
    navigate,
    type,
  ]);

  useEffect(() => {
    if (month && categoryId) {
      setShowingCategoryModal(true);
      return;
    }
    setShowingCategoryModal(false);
  }, [month, categoryId]);

  const togglePin = useCallback(
    async (isPinned: boolean, categoryId: string) => {
      if (!budgetTemplateId || !costCenterId) return;

      return !isPinned
        ? ConsolidatedBudgetAPICaller.pinCategory(
            budgetTemplateId,
            costCenterId,
            categoryId
          )
        : ConsolidatedBudgetAPICaller.unpinCategory(
            budgetTemplateId,
            costCenterId,
            categoryId
          );
    },
    [budgetTemplateId, costCenterId]
  );

  return (
    <>
      <div className="p-s-300">
        <div className="d-flex mb-s-250">
          <div
            className="mr-s-100 mt-s-150 pointer"
            title="Voltar"
            onClick={() => navigate(-1)}
          >
            <Icon>arrow_back</Icon>
          </div>

          <h1 className="col-sm-7">
            {data.costCenter && data.costCenter.name}
          </h1>
          <div className="ml-auto">
            <Input
              type="search"
              placeholder="Pesquisar"
              form={register('search', {
                onChange: (e) => {
                  setExpandAll(!!e.target.value);
                  setFilter(e.target.value);
                },
              })}
              prefixes={
                <InputAddon left>
                  <InputIcon>search</InputIcon>
                </InputAddon>
              }
            />
          </div>
          <div className="ml-auto">
            <Button
              className="ml-s-200 col-sm-5"
              design="filled"
              onClick={() => {
                navigate(
                  `/budget-consolidado/${budgetTemplateId}/${costCenterId}/${type}`
                );
              }}
            >
              Evolução Mensal
            </Button>
          </div>
        </div>

        <div className="mt-s-200">
          <Table
            data={
              data.budgetSummary
                ? [data.budgetSummary, data.realizedSummary]
                : []
            }
            dataKey="id"
            showColumnSelector={false}
          >
            <TableColumn
              minWidth="10rem"
              width="10rem"
              headerColumn
              fromKey="id"
              header=""
            />
            <TableColumn
              fromKey="total"
              header="Ano"
              headerAlign="end"
              textAlign="right"
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
            <TableColumn
              fromKey="q1"
              header="Q1"
              headerAlign="end"
              textAlign="right"
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
            <TableColumn
              fromKey="q2"
              header="Q2"
              headerAlign="end"
              textAlign="right"
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
            <TableColumn
              fromKey="q3"
              header="Q3"
              headerAlign="end"
              textAlign="right"
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
            <TableColumn
              fromKey="q4"
              header="Q4"
              headerAlign="end"
              textAlign="right"
              render={(value) => {
                return formatCurrency(Number(value));
              }}
            />
          </Table>

          <hr style={{ border: '1px solid #E0E0E0' }} />

          <div className="mt-s-200">
            {monthsChipsEnum.map((month) => {
              if (!budgetTemplate?.months.includes(month.key as string)) {
                return null;
              }
              const monthHeader = data?.months?.find(
                (data) => data.month === month.key
              );

              return (
                <Accordion
                  key={month.key}
                  forceOpen={
                    expandAll &&
                    monthData?.[month.key]?.some((d) => {
                      const filterFor = filter.toLowerCase();
                      return (
                        d.name.toLowerCase().includes(filterFor) ||
                        d.businessPartner.toLowerCase().includes(filterFor)
                      );
                    })
                  }
                  header={
                    <>
                      <span className="w-50 d-inline-block">{month.name}</span>
                      <span className="w-50 d-inline-block">
                        <span className="mr-s-300">
                          Previsto:{' '}
                          <span className="font-weight-regular">
                            {formatCurrency(Number(monthHeader?.totalBudget))}
                          </span>
                        </span>
                        <span className="mr-s-300">
                          Realizado:{' '}
                          <span className="font-weight-regular">
                            {formatCurrency(Number(monthHeader?.totalRealized))}
                          </span>
                        </span>
                        <span className="mr-s-300">
                          Diferença:{' '}
                          <span
                            className={getPercentageClass(
                              monthHeader?.percentageUsage || 0,
                              'font-weight-regular'
                            )}
                          >
                            {Number(monthHeader?.percentageUsage).toFixed(2)}%
                          </span>
                        </span>
                      </span>
                    </>
                  }
                >
                  <Table
                    className="mt-s-200 mb-s-200 pointer"
                    transparentHeaders
                    data={
                      monthData?.[month.key]?.filter((d) => {
                        if (!filter) return true;
                        const filterFor = filter.toLowerCase();
                        return (
                          d.name.toLowerCase().includes(filterFor) ||
                          d.businessPartner.toLowerCase().includes(filterFor)
                        );
                      }) || []
                    }
                    dataKey="name"
                    withRedirectIcon
                    showColumnSelector={false}
                    onRowClick={(row) => {
                      // move this to the arrow button onclick function when added
                      const params = new URLSearchParams({
                        month: month.key,
                        categoryId: row.id,
                        categoryName: row.name,
                      }).toString();
                      navigate(`?${params}`, {
                        state: location.state,
                      });
                    }}
                  >
                    <TableColumn
                      className="p-s-100"
                      fromKey="pinned"
                      header=""
                      width="1rem"
                      render={(isPinned, row) => {
                        return (
                          <img
                            className="ml-s-200 table__icon pointer"
                            onClick={(e) => {
                              togglePin(isPinned, row.id).then(() => {
                                if (budgetTemplateId && costCenterId)
                                  loadCostCenterBudget({
                                    budgetTemplateId,
                                    costCenterId,
                                    type,
                                  });
                              });
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            width="24"
                            src={isPinned ? pinned : unpinned}
                            alt="Pinar"
                          />
                        );
                      }}
                    />
                    <TableColumn
                      fromKey="name"
                      header="Categoria"
                      width="25% !important"
                    />
                    <TableColumn
                      fromKey="businessPartner"
                      header="Fornecedor/Cliente"
                      ellipsis={true}
                    />
                    <TableColumn
                      fromKey="totalBudget"
                      header="Previsto"
                      render={(value) => {
                        return formatCurrency(Number(value));
                      }}
                    />
                    <TableColumn
                      fromKey="totalRealized"
                      header="Realizado"
                      render={(value) => {
                        return formatCurrency(Number(value));
                      }}
                    />
                    <TableColumn
                      fromKey="percentageUsage"
                      width="10%"
                      header="Diferença"
                      headerAlign="center"
                      textAlign="right"
                      render={(percentage) => {
                        return (
                          <div className={getPercentageClass(percentage)}>
                            {Number(percentage).toFixed(2)}%
                          </div>
                        );
                      }}
                    />
                  </Table>
                </Accordion>
              );
            })}
          </div>
        </div>
      </div>
      <CategoryDetailModal
        isOpen={showingCategoryModal}
        categoryId={categoryId ?? ''}
        categoryName={categoryName ?? ''}
        month={month ?? ''}
        type={type}
        budgetTemplateId={budgetTemplateId ?? ''}
        costCenterId={costCenterId ?? ''}
      />
    </>
  );
}
